<script lang="ts">
	import type { AsyncActionResult } from '$lib/components/AsyncAction.svelte';
	import SpinnerButton from '$lib/components/SpinnerButton.svelte';
	import { serviceContext } from '$lib/ServiceContext';
	import { Badge } from '$lib/shadcn/components/ui/badge';
	import * as Dialog from '$lib/shadcn/components/ui/dialog';
	import { cn } from '$lib/shadcn/utils';
	import SignInForm from '$lib/SignInForm.svelte';
	import { SignInState } from '$lib/AuthService.svelte';
	import { delay, fetchRetry } from '$lib/utils';
	import { Check } from '@lucide/svelte';
	import { onMount, tick } from 'svelte';
	import z from 'zod';

	let showSignInDialog = $state(false);
	let signingIn = $state(false);
	let mounted = $state(false);
	let {
		title,
		description,
		price,
		priceCents = undefined,
		fullPrice,
		fullPriceCents = undefined,
		salePercentage = undefined,
		duration,
		purchaseId,
		purchaseMode,
		buttonText,
		onclick,
		highlighted = false,
		checkItems = [],
		returnUrl,
		successUrl
	} = $props();

	const ResponseSchema = z.object({ url: z.string() });
	async function signInIfNeeded() {
		await serviceContext.authService.authReady();
		if (serviceContext.authService.getSignInState() == SignInState.SignedIn) {
			return;
		}

		showSignInDialog = true;

		while (showSignInDialog) {
			await delay(250);
		}
		if (serviceContext.authService.getSignInState() != SignInState.SignedIn) {
			throw new Error('Unable to sign in');
		}
	}
	async function initiateCheckout(): AsyncActionResult {
		await signInIfNeeded();
		const formData = new FormData();
		formData.append('lookup_key', purchaseId);
		formData.append('mode', purchaseMode);
		formData.append('return_url', returnUrl);
		formData.append('success_url', successUrl);

		onclick();

		const response = await fetchRetry('/api/checkout/create-checkout-session', {
			body: formData,
			method: 'post'
		});
		const parsedResponse = await z.parseAsync(ResponseSchema, await response.json());
		window.location.href = parsedResponse.url;
		return { keep_loading_state: true };
	}

	onMount(() => {
		mounted = true;
	});
</script>

<div
	class={cn('card preset-tonal-outlined flex max-w-80 flex-col items-center gap-2 p-4 shadow', {
		'border-primary': highlighted
	})}
>
	{#if salePercentage}
		<Badge variant={highlighted ? 'neon' : 'dark'} class="absolute -mt-7"
			>{salePercentage}% OFF</Badge
		>
	{/if}
	<p class="text-2xl">{title}</p>
	<p class="text-muted-foreground text-center">{description}</p>
	<hr class="hr w-full" />
	<div class="flex flex-col">
		{#each checkItems as checkItem}
			<div class="flex items-center gap-2"><Check class="text-primary/80" /> {checkItem}</div>
		{/each}
	</div>
	<hr class="hr w-full" />
	<div class="flex flex-row items-baseline">
		<div class="text-foreground/60 strike-through mr-1 flex flex-row items-baseline self-start">
			<p class="self-start">{fullPrice}</p>
			<p class="self-start text-xs">{fullPriceCents}</p>
			<p class="self-start text-xs">€</p>
		</div>
		<p class="text-4xl font-bold">{price}</p>
		<p class="self-start text-2xl">{priceCents}</p>
		<p class="self-start text-2xl">€</p>
		<p class="text-muted-foreground">{duration}</p>
	</div>
	<SpinnerButton
		variant={highlighted ? 'filled' : 'tonal'}
		id="checkout-and-portal-button"
		class="flex w-full justify-center"
		action={initiateCheckout}
		disabled={!mounted}
		>{buttonText}
	</SpinnerButton>
</div>

<Dialog.Root bind:open={showSignInDialog}>
	<Dialog.Portal>
		<Dialog.Overlay inert>
			<div class="fixed top-0 left-0 h-full w-full bg-black opacity-30"></div>
		</Dialog.Overlay>
		<Dialog.Content
			showCloseButton={false}
			escapeKeydownBehavior={signingIn ? 'ignore' : 'close'}
			interactOutsideBehavior={signingIn ? 'ignore' : 'close'}
			class="xs:max-w-[calc(100%-2rem)] xs:w-auto fixed top-[50%] left-[50%] z-50 flex w-full translate-x-[-50%] translate-y-[-50%] p-0"
		>
			<SignInForm
				onBegin={async () => {
					signingIn = true;
					await tick();
				}}
				onSuccess={async () => {
					showSignInDialog = false;
					signingIn = false;
				}}
				onFailure={async () => {
					showSignInDialog = false;
					signingIn = false;
				}}
			/>
		</Dialog.Content>
	</Dialog.Portal>
</Dialog.Root>
